/**
 * CitationLibraryPanel Component
 * Displays saved citations from the library and allows insertion
 */

import React, { useState, useEffect, useCallback } from 'react'
import { log, logError, logDebug } from '@utils/logger'
import {
  BookOpenIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  TrashIcon,
  PencilIcon,
  FunnelIcon,
  ArrowPathIcon,
  BookmarkIcon,
  DocumentArrowDownIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { citationLibraryAPI, type CitationLibraryItem } from '@/api/citationLibrary'
import { generateBibtexKey } from '@/utils/generateBibtex'
import type { CitationPaper } from '@/api/citations'
import ConfirmDialog from '@components/common/ConfirmDialog'

interface CitationLibraryPanelProps {
  documentId: string
  documentMode?: 'markdown' | 'latex' | 'plain'
  onCitationInsert?: (citation: string, paper?: CitationPaper) => void
  isOpen: boolean
  onClose: () => void
}

export const CitationLibraryPanel: React.FC<CitationLibraryPanelProps> = ({
  documentId,
  documentMode = 'markdown',
  onCitationInsert,
  isOpen,
  onClose,
}) => {
  const [citations, setCitations] = useState<CitationLibraryItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<'created_at' | 'last_used_at' | 'times_used' | 'title' | 'year'>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)
  const [expandedCitationId, setExpandedCitationId] = useState<string | null>(null)
  const isLaTeX = documentMode === 'latex'

  // Load citations
  const loadCitations = useCallback(async () => {
    if (!documentId) return
    
    setIsLoading(true)
    try {
      const data = await citationLibraryAPI.getCitations(documentId, {
        search: searchQuery || undefined,
        tags: selectedTags.length > 0 ? selectedTags.join(',') : undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      })
      logDebug('[CitationLibraryPanel] Loaded citations:', data)
      logDebug('[CitationLibraryPanel] Citations count:', data?.length || 0)
      setCitations(data || [])
    } catch (error) {
      logError('Error loading citations:', error)
      setCitations([])
    } finally {
      setIsLoading(false)
    }
  }, [documentId, searchQuery, sortBy, sortOrder, selectedTags])

  useEffect(() => {
    if (isOpen && documentId) {
      loadCitations()
    } else {
      // Reset citations when panel is closed
      setCitations([])
    }
  }, [isOpen, documentId, loadCitations])

  const handleInsertCitation = async (citation: CitationLibraryItem) => {
    if (!onCitationInsert) return

    try {
      // Mark as used
      await citationLibraryAPI.markCitationUsed(documentId, citation.id)

      // Convert to CitationPaper format
      const paper: CitationPaper = {
        title: citation.title,
        authors: citation.authors,
        year: citation.year,
        doi: citation.doi,
        pmid: citation.pmid,
        arxiv_id: citation.arxiv_id,
        abstract: citation.abstract,
        venue: citation.venue,
        url: citation.url,
        bibtexKey: citation.bibtex_key,
      }

      if (isLaTeX && citation.bibtex_key) {
        // For LaTeX, pass paper object with bibtexKey
        onCitationInsert('', paper)
      } else {
        // For non-LaTeX, format citation text
        const authors = citation.authors.length > 0
          ? citation.authors.length === 1
            ? citation.authors[0]
            : citation.authors.length <= 6
            ? citation.authors.join(', ')
            : `${citation.authors.slice(0, 5).join(', ')}, et al.`
          : 'Unknown Author'
        const year = citation.year ? ` (${citation.year}).` : '.'
        const citationText = `${authors}${year} ${citation.title}. ${citation.venue ? citation.venue + '. ' : ''}${citation.doi ? `https://doi.org/${citation.doi}` : citation.url || ''}`
        onCitationInsert(citationText, paper)
      }

      // Reload to update usage stats
      loadCitations()
    } catch (error) {
      logError('Error inserting citation:', error)
    }
  }

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [citationToDelete, setCitationToDelete] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDeleteCitation = (citationId: string) => {
    setCitationToDelete(citationId)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!citationToDelete) return

    setIsDeleting(true)
    try {
      await citationLibraryAPI.deleteCitation(documentId, citationToDelete)
      loadCitations()
      setDeleteConfirmOpen(false)
      setCitationToDelete(null)
    } catch (error) {
      logError('Error deleting citation:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleExportBibTeX = async () => {
    try {
      const { content, filename } = await citationLibraryAPI.exportBibTeX(documentId)
      
      // Create download link
      const blob = new Blob([content], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
    } catch (error) {
      logError('Error exporting BibTeX:', error)
    }
  }

  // Get all unique tags
  const allTags = Array.from(new Set((citations || []).flatMap(c => c.tags || [])))

  if (!isOpen) return null

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <BookmarkIcon className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Citation Library
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            ({(citations || []).length})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportBibTeX}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Export as BibTeX"
          >
            <DocumentArrowDownIcon className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search citations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="created_at">Date Added</option>
            <option value="last_used_at">Last Used</option>
            <option value="times_used">Most Used</option>
            <option value="title">Title</option>
            <option value="year">Year</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        {/* Tags */}
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => {
                  setSelectedTags(prev =>
                    prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
                  )
                }}
                className={`px-2 py-1 text-xs rounded ${
                  selectedTags.includes(tag)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Citations List */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <ArrowPathIcon className="w-8 h-8 text-blue-600 animate-spin mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Loading citations...</p>
          </div>
        ) : (citations || []).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <BookOpenIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              {searchQuery || selectedTags.length > 0
                ? 'No citations match your filters'
                : 'No citations saved yet. Save citations from suggestions to build your library.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {(citations || []).map(citation => {
              const isExpanded = expandedCitationId === citation.id
              return (
                <div
                  key={citation.id}
                  className="border rounded-lg border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                >
                  {/* Collapsed View */}
                  <div 
                    className="p-3 cursor-pointer"
                    onClick={() => setExpandedCitationId(isExpanded ? null : citation.id)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                          {citation.title}
                        </h3>
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <span>{citation.authors.slice(0, 2).join(', ')}{citation.authors.length > 2 ? ' et al.' : ''}</span>
                          {citation.year && <span>• {citation.year}</span>}
                          {citation.venue && <span>• {citation.venue}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {citation.times_used > 0 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Used {citation.times_used}x
                          </span>
                        )}
                        <ChevronDownIcon 
                          className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
                        />
                      </div>
                    </div>

                    {/* Tags */}
                    {citation.tags && citation.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {citation.tags.map(tag => (
                          <span
                            key={tag}
                            className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Expanded View */}
                  {isExpanded && (
                    <div className="px-3 pb-3 pt-0 border-t border-gray-200 dark:border-gray-700 space-y-3">
                      {/* Abstract */}
                      {citation.abstract && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Abstract</h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                            {citation.abstract}
                          </p>
                        </div>
                      )}

                      {/* Full Author List */}
                      <div>
                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Authors</h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {citation.authors.join(', ')}
                        </p>
                      </div>

                      {/* Additional Details */}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {citation.year && (
                          <div>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Year: </span>
                            <span className="text-gray-600 dark:text-gray-400">{citation.year}</span>
                          </div>
                        )}
                        {citation.venue && (
                          <div>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Venue: </span>
                            <span className="text-gray-600 dark:text-gray-400">{citation.venue}</span>
                          </div>
                        )}
                        {citation.doi && (
                          <div>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">DOI: </span>
                            <a 
                              href={`https://doi.org/${citation.doi}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {citation.doi}
                            </a>
                          </div>
                        )}
                        {citation.pmid && (
                          <div>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">PMID: </span>
                            <a 
                              href={`https://pubmed.ncbi.nlm.nih.gov/${citation.pmid}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {citation.pmid}
                            </a>
                          </div>
                        )}
                        {citation.arxiv_id && (
                          <div>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">arXiv: </span>
                            <a 
                              href={`https://arxiv.org/abs/${citation.arxiv_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {citation.arxiv_id}
                            </a>
                          </div>
                        )}
                        {citation.url && (
                          <div className="col-span-2">
                            <span className="font-semibold text-gray-700 dark:text-gray-300">URL: </span>
                            <a 
                              href={citation.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 dark:text-blue-400 hover:underline break-all"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {citation.url}
                              </a>
                            </div>
                        )}
                        {citation.bibtex_key && (
                          <div>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">BibTeX Key: </span>
                            <span className="text-gray-600 dark:text-gray-400 font-mono">{citation.bibtex_key}</span>
                          </div>
                        )}
                        {citation.times_used > 0 && (
                          <div>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Times Used: </span>
                            <span className="text-gray-600 dark:text-gray-400">{citation.times_used}</span>
                          </div>
                        )}
                        {citation.last_used_at && (
                          <div>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Last Used: </span>
                            <span className="text-gray-600 dark:text-gray-400">
                              {new Date(citation.last_used_at).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        {citation.created_at && (
                          <div>
                            <span className="font-semibold text-gray-700 dark:text-gray-300">Added: </span>
                            <span className="text-gray-600 dark:text-gray-400">
                              {new Date(citation.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      {citation.notes && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Notes</h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                            {citation.notes}
                          </p>
                        </div>
                      )}

                      {/* BibTeX Entry */}
                      {citation.bibtex_entry && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">BibTeX Entry</h4>
                          <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded overflow-x-auto font-mono text-gray-700 dark:text-gray-300">
                            {citation.bibtex_entry}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div 
                    className="flex items-center gap-2 px-3 pb-3 pt-2 border-t border-gray-200 dark:border-gray-700"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => handleInsertCitation(citation)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      <CheckIcon className="w-3 h-3" />
                      {isLaTeX ? 'Insert' : 'Insert'}
                    </button>
                    <button
                      onClick={() => handleDeleteCitation(citation.id)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                    >
                      <TrashIcon className="w-3 h-3" />
                      Delete
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
          setCitationToDelete(null)
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Citation"
        message="Are you sure you want to delete this citation from your library? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  )
}

